import i18next from 'i18next';
import { educationData } from '../data/education.js';

/**
 * Setup education abstract toggle functionality
 */
export function setupEducationAbstracts() {
    const educationItems = document.querySelectorAll('.timeline-item');
    
    educationItems.forEach(item => {
        // Check if this is an education item (has university logo)
        const universityLogo = item.querySelector('.university-logo');
        if (!universityLogo) return;
        
        // Find the education meta section
        const educationMeta = item.querySelector('.education-meta');
        if (!educationMeta) return;
        
        // Try to identify which education item this is based on any i18n key in the item
        const allI18nElements = item.querySelectorAll('[data-i18n]');
        let eduKey = null;
        
        for (const element of allI18nElements) {
            const i18nKey = element.getAttribute('data-i18n');
            if (i18nKey && i18nKey.includes('experience.edu')) {
                // Extract education item key (e.g., 'edu1', 'edu2', etc.)
                const keyParts = i18nKey.split('.');
                if (keyParts.length >= 2) {
                    const possibleKey = keyParts[1];
                    if (educationData[possibleKey]) {
                        eduKey = possibleKey;
                        break;
                    }
                }
            }
        }
        
        if (!eduKey) return;
        
        // Get abstract data
        const currentLang = i18next?.language || 'en';
        const abstractText = currentLang === 'pl' ? 
            educationData[eduKey].abstractPl : 
            educationData[eduKey].abstractEn;
        
        // Only add abstract functionality if abstract exists
        if (!abstractText || abstractText.trim() === '') return;
        
        // Create abstract container
        const abstractContainer = document.createElement('div');
        abstractContainer.className = 'education-abstract-container';
        
        // Create abstract toggle button (same style as publications)
        const abstractButton = document.createElement('button');
        abstractButton.className = 'abstract-toggle';
        abstractButton.setAttribute('aria-expanded', 'false');
        abstractButton.setAttribute('data-education-key', eduKey);
        abstractButton.textContent = i18next.t('experience.labels.showAbstract');
        
        // Create abstract content div (same style as publications)
        const abstractContent = document.createElement('div');
        abstractContent.className = 'publication-abstract';
        abstractContent.setAttribute('hidden', '');
        abstractContent.setAttribute('data-education-key', eduKey);
        abstractContent.textContent = abstractText;
        
        // Add click event listener (same logic as publications)
        abstractButton.addEventListener('click', function() {
            const isExpanded = this.getAttribute('aria-expanded') === 'true';
            
            if (isExpanded) {
                abstractContent.setAttribute('hidden', '');
                this.setAttribute('aria-expanded', 'false');
                this.textContent = i18next.t('experience.labels.showAbstract');
            } else {
                abstractContent.removeAttribute('hidden');
                this.setAttribute('aria-expanded', 'true');
                this.textContent = i18next.t('experience.labels.hideAbstract');
            }
        });
        
        // Append button and content to container
        abstractContainer.appendChild(abstractButton);
        abstractContainer.appendChild(abstractContent);
        
        // Insert container after the education meta
        educationMeta.parentNode.insertBefore(abstractContainer, educationMeta.nextSibling);
    });
}

/**
 * Update abstract content and button text when language changes
 */
export function updateEducationAbstracts() {
    const abstractButtons = document.querySelectorAll('.abstract-toggle[data-education-key]');
    const abstractContents = document.querySelectorAll('.publication-abstract[data-education-key]');
    
    const currentLang = i18next?.language || 'en';
    
    // Update button texts
    abstractButtons.forEach(button => {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
            button.textContent = i18next.t('experience.labels.hideAbstract');
        } else {
            button.textContent = i18next.t('experience.labels.showAbstract');
        }
    });
    
    // Update abstract content
    abstractContents.forEach(abstractContent => {
        const eduKey = abstractContent.getAttribute('data-education-key');
        if (!eduKey || !educationData[eduKey]) return;
        
        const abstractText = currentLang === 'pl' ? 
            educationData[eduKey].abstractPl : 
            educationData[eduKey].abstractEn;
        
        if (abstractText && abstractText.trim() !== '') {
            abstractContent.textContent = abstractText;
        }
    });
}

// Setup language change listener
let educationLanguageListenerAttached = false;
if (!educationLanguageListenerAttached && typeof i18next !== 'undefined' && i18next?.on) {
    i18next.on('languageChanged', () => {
        updateEducationAbstracts();
    });
    educationLanguageListenerAttached = true;
}
